import React from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { extractPlainText } from '../utils/richTextRenderer';
import { getImageUrl } from '../services/api';

const ProductRangeSection = () => {
  const { products, loading, error } = useProducts();

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-catalina-blue"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-red-600">
            <p>Error loading products: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-600">
            <p>No products available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  // Filter to display only specific products from API
  const targetProducts = ['telescopic ladders', 'platform trolley', 'pallet truck'];
  const displayProducts = products.filter(product => 
    targetProducts.some(name => 
      product.Title?.toLowerCase().includes(name.toLowerCase())
    )
  );

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-catalina-blue mb-4">Our Product Range</h2>
          <p className="text-xl text-gray-600">
            Discover our comprehensive selection of professional-grade material handling equipment
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {displayProducts.map((product, index) => {
            const imageUrl = product.Image ? getImageUrl(product.Image, 'medium') : null;
            
            return (
              <div 
                key={product.id} 
                className="bg-gray-50 rounded-lg shadow-md hover:shadow-xl transition duration-300 overflow-hidden"
              >
                {/* Product Image Section */}
                <div className="bg-white p-8 flex items-center justify-center h-80">
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={product.Image?.alternativeText || product.Title}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <svg className="w-16 h-16 text-catalina-blue" viewBox="0 0 100 100" fill="currentColor">
                          <path d="M20 80 L35 20 L50 80 L65 20 L80 80" stroke="currentColor" strokeWidth="8" fill="none"/>
                          <path d="M35 20 L50 80" stroke="#FF8C00" strokeWidth="8" fill="none"/>
                        </svg>
                      </div>
                      <div className="font-bold text-catalina-blue text-xl">
                        TRO<span className="text-tango">LIFT</span>
                      </div>
                      <div className="text-sm text-gray-600 tracking-wider">SOLUTIONS</div>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-catalina-blue mb-3">{product.Title}</h3>
                  {product.Description && (
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {extractPlainText(product.Description, 100)}
                    </p>
                  )}
                  <Link
                    to={product.Link || `/product/${product.slug}`}
                    className="bg-catalina-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-catalina-blue/90 transition duration-300 inline-block w-full text-center"
                  >
                    {index === 3 ? 'VIEW ALL PRODUCTS' : `EXPLORE ${product.Title.toUpperCase()}`}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductRangeSection;